import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Settings, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    platform: 'shopify',
    name: '',
    credentials: {},
    webhook_url: '',
  });
  const [credentialFields, setCredentialFields] = useState({});

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const platformFields = {
    shopify: ['shop_url', 'api_key', 'api_secret'],
    woocommerce: ['store_url', 'consumer_key', 'consumer_secret'],
    whatsapp: ['phone_number', 'api_token'],
  };

  const handlePlatformChange = (platform) => {
    setFormData({ ...formData, platform });
    const fields = {};
    platformFields[platform].forEach((field) => {
      fields[field] = '';
    });
    setCredentialFields(fields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        credentials: credentialFields,
      };
      await axios.post(`${API}/integrations`, payload);
      toast.success('Integration created successfully!');
      setDialogOpen(false);
      resetForm();
      fetchIntegrations();
    } catch (error) {
      console.error('Failed to create integration:', error);
      toast.error('Failed to create integration');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      await axios.delete(`${API}/integrations/${id}`);
      toast.success('Integration deleted successfully!');
      fetchIntegrations();
    } catch (error) {
      console.error('Failed to delete integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  const resetForm = () => {
    setFormData({
      platform: 'shopify',
      name: '',
      credentials: {},
      webhook_url: '',
    });
    setCredentialFields({});
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      shopify: '🛍️',
      woocommerce: '🛒',
      whatsapp: '📱',
    };
    return icons[platform] || '🔗';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Integrations</h1>
          <p className="text-slate-600 mt-2">Connect your e-commerce platforms</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="add-integration-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Integration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Integration</DialogTitle>
              <DialogDescription>Connect a new e-commerce platform</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform *</Label>
                <Select
                  value={formData.platform}
                  onValueChange={handlePlatformChange}
                >
                  <SelectTrigger data-testid="integration-platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shopify">Shopify</SelectItem>
                    <SelectItem value="woocommerce">WooCommerce</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Integration Name *</Label>
                <Input
                  id="name"
                  placeholder="My Shopify Store"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="integration-name-input"
                />
              </div>

              {/* Dynamic credential fields */}
              {platformFields[formData.platform]?.map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={field} className="capitalize">
                    {field.replace(/_/g, ' ')} *
                  </Label>
                  <Input
                    id={field}
                    placeholder={field.replace(/_/g, ' ')}
                    value={credentialFields[field] || ''}
                    onChange={(e) =>
                      setCredentialFields({ ...credentialFields, [field]: e.target.value })
                    }
                    required
                    data-testid={`integration-${field}-input`}
                  />
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="webhook_url">Webhook URL (Optional)</Label>
                <Input
                  id="webhook_url"
                  placeholder="https://your-store.com/webhook"
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  data-testid="integration-webhook-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="integration-submit-button">
                Create Integration
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 hover:shadow-lg transition-all cursor-pointer">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="text-4xl">🛍️</div>
              <div>
                <CardTitle>Shopify</CardTitle>
                <CardDescription>Connect your Shopify store</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Sync orders automatically from your Shopify store
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-lg transition-all cursor-pointer">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="text-4xl">🛒</div>
              <div>
                <CardTitle>WooCommerce</CardTitle>
                <CardDescription>Connect your WooCommerce site</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Import orders from your WooCommerce store
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-lg transition-all cursor-pointer">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="text-4xl">📱</div>
              <div>
                <CardTitle>WhatsApp</CardTitle>
                <CardDescription>Receive orders via WhatsApp</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Handle orders directly from WhatsApp messages
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active Integrations */}
      {integrations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Integrations</CardTitle>
            <CardDescription>{integrations.length} integration(s) configured</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {integrations.map((integration) => (
                <div
                  key={integration.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{getPlatformIcon(integration.platform)}</div>
                    <div>
                      <div className="font-semibold text-slate-900">{integration.name}</div>
                      <div className="text-sm text-slate-600 capitalize">
                        {integration.platform}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(integration.id)}
                      data-testid={`delete-integration-${integration.platform}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-[#1680e5]" />
            How Integrations Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>
            • Connect your e-commerce platforms to automatically receive orders in your WMS
          </p>
          <p>• Orders are synced in real-time via webhooks</p>
          <p>• Configure API credentials from your platform's settings</p>
          <p>• Each integration requires proper API keys and permissions</p>
        </CardContent>
      </Card>
    </div>
  );
}
